/* Admin review page for Teacher Availability Requests.
   Shows pending requests with affected-class impact scan, lets the
   admin/super_admin/owner approve / reject / request amendment. */
import { useEffect, useState } from "react";
import api, { useList, errMsg, DAYS } from "@/lib/api";
import { PageHeader, Spinner, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, MessageCircleQuestion, Clock, AlertTriangle, ShieldCheck } from "lucide-react";

const REQ_TYPE_BADGE = {
  new_teacher: { cls: "bg-violet-100 text-violet-800 border-violet-200", label: "New teacher" },
  add_time: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Add more time" },
  change_time: { cls: "bg-amber-100 text-amber-800 border-amber-200", label: "Change slot" },
  remove_time: { cls: "bg-rose-100 text-rose-800 border-rose-200", label: "Remove slot" },
};

function ReqTypeChip({ type }) {
  const t = REQ_TYPE_BADGE[type || "add_time"] || REQ_TYPE_BADGE.add_time;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${t.cls}`}>
      {t.label}
    </span>
  );
}

function ImpactBlock({ requestId }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/availability-requests/${requestId}/impact`)
      .then((r) => { if (!cancelled) setImpact(r.data); })
      .catch(() => { if (!cancelled) setImpact(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [requestId]);

  if (loading) return <p className="text-xs text-muted-foreground">Checking affected classes...</p>;
  if (!impact) return null;
  const total = impact.total_affected || 0;

  if (total === 0) {
    return (
      <p className="text-xs text-emerald-700 flex items-center gap-1.5"
         data-testid={`impact-clear-${requestId}`}>
        <ShieldCheck className="h-3.5 w-3.5" />
        No booked classes in this time window — safe to approve.
      </p>
    );
  }

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/60 p-3"
         data-testid={`impact-warning-${requestId}`}>
      <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5" />
        {total} booked/reserved item{total > 1 ? "s" : ""} in this time window — review before approving:
      </p>
      <div className="mt-2 space-y-1 text-[11px] text-amber-900/90">
        {(impact.affected_classes || []).map((c) => (
          <div key={c.id} data-testid={`affected-class-${c.id}`}>
            • {c.student_name} · {c.course_name} · {c.start_time}–{c.end_time}
            <span className="text-amber-700 ml-1">[{c.status}]</span>
          </div>
        ))}
        {(impact.affected_bookings || []).map((b) => (
          <div key={b.id} data-testid={`affected-booking-${b.id}`}>
            • {b.booking_date} {b.start_time}–{b.end_time} · {b.student_name || "—"}
            <span className="text-amber-700 ml-1">[{b.booking_type}]</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminAvailabilityRequestsPage() {
  const { data: requests, loading, reload } = useList("/admin/availability-requests");
  const [target, setTarget] = useState(null);  // {row, decision}
  const [adminNote, setAdminNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return <Spinner full />;
  const pending = (requests || []).filter((r) =>
    r.status === "pending" || r.status === "amendment_requested");
  const decided = (requests || []).filter((r) =>
    r.status !== "pending" && r.status !== "amendment_requested");

  const decide = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/availability-requests/${target.row.id}/decide`, {
        decision: target.decision,
        admin_note: adminNote || null,
      });
      const verb = target.decision === "approve" ? "approved"
                : target.decision === "reject" ? "rejected"
                : "amendment requested";
      toast.success(`Request ${verb}`);
      setTarget(null); setAdminNote("");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-availability-requests-page">
      <PageHeader title="Teacher availability requests"
        description="Review and approve new teacher availability, add-more-time, change, and remove requests." />

      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-amber-800">
        <Clock className="h-4 w-4" />Pending review ({pending.length})
      </h3>
      {pending.length === 0
        ? <div className="mb-8"><EmptyState text="No pending availability requests." /></div>
        : <div className="space-y-3 mb-8" data-testid="pending-requests">
            {pending.map((r) => (
              <div key={r.id} className="rounded-lg border bg-card p-4"
                   data-testid={`admin-availability-row-${r.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <p className="text-sm font-semibold">{r.teacher_name}</p>
                      <ReqTypeChip type={r.request_type} />
                      {r.status === "amendment_requested" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200">
                          Amendment requested
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {DAYS[r.day_of_week]} · {r.start_time}–{r.end_time}
                      {r.instrument && ` · ${r.instrument}`}
                      {r.effective_from && ` · from ${r.effective_from}`}
                      {r.branch && ` · ${r.branch}`}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Submitted by {r.submitted_by_role || "teacher"} on {r.created_at?.slice(0, 10)}
                    </p>
                    {r.note && (
                      <p className="text-xs italic text-muted-foreground mt-1">&quot;{r.note}&quot;</p>
                    )}
                    {r.admin_note && r.status === "amendment_requested" && (
                      <p className="text-xs text-orange-800 mt-1">
                        Previous admin note: {r.admin_note}
                      </p>
                    )}
                    <div className="mt-3">
                      <ImpactBlock requestId={r.id} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <Button size="sm" onClick={() => setTarget({ row: r, decision: "approve" })}
                            data-testid={`approve-availability-${r.id}`}>
                      <Check className="h-4 w-4 mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => setTarget({ row: r, decision: "amend" })}
                            data-testid={`amend-availability-${r.id}`}>
                      <MessageCircleQuestion className="h-4 w-4 mr-1" />Request amendment
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => setTarget({ row: r, decision: "reject" })}
                            data-testid={`reject-availability-${r.id}`}>
                      <X className="h-4 w-4 mr-1" />Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>}

      <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Decided ({decided.length})</h3>
      {decided.length === 0
        ? <EmptyState text="No decided requests yet." />
        : <div className="space-y-2" data-testid="decided-requests">
            {decided.slice(0, 50).map((r) => (
              <div key={r.id} className="rounded-md border p-3 flex flex-wrap items-start justify-between gap-3"
                   data-testid={`decided-availability-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{r.teacher_name}</p>
                    <ReqTypeChip type={r.request_type} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {DAYS[r.day_of_week]} · {r.start_time}–{r.end_time}
                  </p>
                  {r.admin_note && <p className="text-xs text-muted-foreground mt-0.5">Admin note: {r.admin_note}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">{r.decided_at?.slice(0, 10)}</p>
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>}

      <Dialog open={!!target} onOpenChange={(o) => !o && (setTarget(null), setAdminNote(""))}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {target?.decision === "approve" ? "Approve request"
                : target?.decision === "reject" ? "Reject request"
                : "Request amendment"}
            </DialogTitle>
            <DialogDescription>
              {target?.row.teacher_name} · {target?.row.request_type?.replace("_", " ")} ·{" "}
              {target?.row.start_time}–{target?.row.end_time}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {target?.decision === "amend"
                ? "Tell the teacher what to amend (required)"
                : "Internal note (optional)"}
            </Label>
            <Textarea rows={3} value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                      data-testid="decision-admin-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} data-testid="decision-cancel-btn">Cancel</Button>
            <Button onClick={decide}
                    disabled={busy || (target?.decision === "amend" && !adminNote.trim())}
                    data-testid="decision-confirm-btn">
              {busy ? "Saving…"
                : target?.decision === "approve" ? "Confirm approve"
                : target?.decision === "reject" ? "Confirm reject"
                : "Send amendment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
